// @ts-nocheck

import React, { useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';

import Formatter from '../../i18n/formatter';
import { getCurrency } from '../../i18n/l10n';
import NumberUtils from '../../utils/number';

export const DEFAULT_ANIMATED_DIGITS_LABEL_TRANSITION_DURATION = 1000; // ms

const changeAmountText = (node: HTMLDivElement, rawValue: number, formatFn: (p1?: unknown, p2?: unknown) => void) => {
  node.innerText = !NumberUtils.checkIsNaN(rawValue) ? formatFn(rawValue, getCurrency()) : '';
};

const Amount = styled.div`
  position: relative;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  flex: 0 0 auto;
  font-weight: bold;
`;

type AnimatedDigitsLabelProps = {
  current: number;
  next: number;
  formatFn?: (p1?: unknown, p2?: unknown) => void;
  lastFormatFn?: (p1?: unknown, p2?: unknown) => void;
  timeout?: number;
  className?: string;
};

const AnimatedDigitsLabel = ({
  current,
  next,
  formatFn = Formatter.formatBankroll,
  lastFormatFn = Formatter.formatBankroll,
  timeout = DEFAULT_ANIMATED_DIGITS_LABEL_TRANSITION_DURATION,
  className,
}: AnimatedDigitsLabelProps) => {
  const amountRef = useRef<typeof Amount>();
  const currRAFPid = useRef(null);

  useLayoutEffect(() => {
    const node = amountRef.current;

    if (!node?.innerText && typeof next === 'number') {
      changeAmountText(node, next, formatFn);
    }
  }, [next, formatFn]);

  useLayoutEffect(() => {
    if (typeof current === 'number' && typeof next === 'number') {
      const diff = next - current;

      if (diff) {
        const startTime = performance.now();
        let counter = current;

        const RAFCallback = () => {
          const elapsedTime = performance.now() - startTime;
          const progressRatio = elapsedTime / timeout;

          if (amountRef.current) {
            if (elapsedTime < timeout) {
              counter = current + diff * progressRatio;
              changeAmountText(amountRef.current, counter, formatFn);

              requestAnimationFrame(RAFCallback);
            }

            if (elapsedTime >= timeout) {
              counter = next;
              changeAmountText(amountRef.current, counter, lastFormatFn);
            }
          }
        };

        currRAFPid.current = requestAnimationFrame(RAFCallback);
      }
    }
  }, [current, next, formatFn, timeout, lastFormatFn]);

  useLayoutEffect(() => {
    return () => {
      if (currRAFPid.current) {
        window.cancelAnimationFrame(currRAFPid.current);
      }
    };
  }, []);

  return <Amount className={className} ref={amountRef} />;
};

export default AnimatedDigitsLabel;
